// AddCAUser.js - Admin Component to Create CA Users
import React, { useState } from 'react';
import { initializeApp } from 'firebase/app';
import { getAuth, createUserWithEmailAndPassword } from 'firebase/auth';
import { ref, set } from 'firebase/database';
import { auth, database } from '../../firebase/config';
import { toast } from 'react-toastify';

// Firebase config for secondary auth instance
const firebaseConfig = {
    apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
    authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
    databaseURL: process.env.REACT_APP_FIREBASE_DATABASE_URL,
    projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
    storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
    appId: process.env.REACT_APP_FIREBASE_APP_ID,
};

// Create a secondary Firebase app instance for user creation
const secondaryApp = initializeApp(firebaseConfig, 'Secondary');
const secondaryAuth = getAuth(secondaryApp);

const AddCAUser = () => {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        password: '',
        confirmPassword: ''
    });
    const [loading, setLoading] = useState(false);

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Validation
        if (formData.password !== formData.confirmPassword) {
            toast.error('Passwords do not match!');
            return;
        }

        if (formData.password.length < 6) {
            toast.error('Password must be at least 6 characters!');
            return;
        }

        setLoading(true);

        try {
            // Get current admin user info
            const currentUser = auth.currentUser;

            // Create CA user using SECONDARY auth instance (won't affect current user session)
            const userCredential = await createUserWithEmailAndPassword(
                secondaryAuth,  // Using secondary auth instance
                formData.email,
                formData.password
            );

            const newUser = userCredential.user;

            // Add user data to Realtime Database with CA role
            const userRef = ref(database, `HTAMS/users/${newUser.uid}`);
            await set(userRef, {
                uid: newUser.uid,
                name: formData.name,
                email: formData.email,
                role: 'ca', // Setting role as CA
                createdAt: new Date().toISOString(),
                createdBy: currentUser?.email || 'admin',
                status: 'active'
            });

            // Sign out from secondary auth (doesn't affect primary auth)
            await secondaryAuth.signOut();

            toast.success('✅ CA User created successfully!');

            // Clear form
            setFormData({
                name: '',
                email: '',
                password: '',
                confirmPassword: ''
            });

            console.log('CA User Created:', {
                uid: newUser.uid,
                email: formData.email,
                name: formData.name,
                role: 'ca'
            });

            // Show success message with credentials
            toast.info(`📧 Email: ${formData.email}\n🔑 Password: ${formData.password}`, {
                autoClose: 8000
            });

        } catch (error) {
            console.error('Error creating CA user:', error);

            if (error.code === 'auth/email-already-in-use') {
                toast.error('Email already in use!');
            } else if (error.code === 'auth/invalid-email') {
                toast.error('Invalid email address!');
            } else if (error.code === 'auth/weak-password') {
                toast.error('Password is too weak!');
            } else {
                toast.error('Failed to create CA user. Please try again.');
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div style={styles.container}>
            <div style={styles.header}>
                <h2 style={styles.title}>Add CA User</h2>
                <p style={styles.subtitle}>Create a new Chartered Accountant user account</p>
            </div>

            <form onSubmit={handleSubmit} style={styles.form}>
                <div style={styles.formGroup}>
                    <label htmlFor="name" style={styles.label}>Full Name *</label>
                    <input
                        type="text"
                        id="name"
                        name="name"
                        style={styles.input}
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="Enter CA full name"
                        disabled={loading}
                        required
                    />
                </div>

                <div style={styles.formGroup}>
                    <label htmlFor="email" style={styles.label}>Email Address *</label>
                    <input
                        type="email"
                        id="email"
                        name="email"
                        style={styles.input}
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="ca@example.com"
                        disabled={loading}
                        required
                    />
                </div>

                <div style={styles.formGroup}>
                    <label htmlFor="password" style={styles.label}>Password *</label>
                    <input
                        type="password"
                        id="password"
                        name="password"
                        style={styles.input}
                        value={formData.password}
                        onChange={handleChange}
                        placeholder="Minimum 6 characters"
                        disabled={loading}
                        required
                        minLength={6}
                    />
                </div>

                <div style={styles.formGroup}>
                    <label htmlFor="confirmPassword" style={styles.label}>Confirm Password *</label>
                    <input
                        type="password"
                        id="confirmPassword"
                        name="confirmPassword"
                        style={styles.input}
                        value={formData.confirmPassword}
                        onChange={handleChange}
                        placeholder="Re-enter password"
                        disabled={loading}
                        required
                        minLength={6}
                    />
                </div>

                <button
                    type="submit"
                    style={{
                        ...styles.button,
                        opacity: loading ? 0.7 : 1,
                        cursor: loading ? 'not-allowed' : 'pointer'
                    }}
                    disabled={loading}
                >
                    {loading ? '⏳ Creating CA User...' : '✅ Create CA User'}
                </button>

                <div style={styles.infoBox}>
                    <strong>ℹ️ Important:</strong>
                    <p style={styles.infoText}>
                        This will create a user with <strong>CA role</strong> in Firebase Authentication
                        and Realtime Database. The user can then login through the CA Portal at <code>/ca-login</code>.
                    </p>
                </div>
            </form>
        </div>
    );
};

const styles = {
    container: {
        maxWidth: '600px',
        margin: '0 auto',
        padding: '20px',
        backgroundColor: '#ffffff',
        borderRadius: '12px',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
    },
    header: {
        marginBottom: '30px',
        textAlign: 'center'
    },
    title: {
        fontSize: '2rem',
        fontWeight: '700',
        color: '#1f2937',
        marginBottom: '8px',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        backgroundClip: 'text'
    },
    subtitle: {
        color: '#6b7280',
        fontSize: '0.95rem',
        margin: 0
    },
    form: {
        display: 'flex',
        flexDirection: 'column',
        gap: '20px'
    },
    formGroup: {
        display: 'flex',
        flexDirection: 'column',
        gap: '8px'
    },
    label: {
        fontSize: '0.9rem',
        fontWeight: '600',
        color: '#374151'
    },
    input: {
        width: '100%',
        padding: '12px 16px',
        border: '2px solid #e5e7eb',
        borderRadius: '8px',
        fontSize: '1rem',
        transition: 'border-color 0.15s ease',
        backgroundColor: '#f9fafb',
        boxSizing: 'border-box'
    },
    button: {
        width: '100%',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        color: 'white',
        padding: '14px',
        borderRadius: '8px',
        border: 'none',
        fontSize: '1.05rem',
        fontWeight: '600',
        transition: 'transform 0.15s ease',
        marginTop: '10px'
    },
    infoBox: {
        backgroundColor: '#eff6ff',
        border: '1px solid #bfdbfe',
        borderRadius: '8px',
        padding: '16px',
        marginTop: '10px'
    },
    infoText: {
        color: '#1e40af',
        fontSize: '0.875rem',
        lineHeight: '1.5',
        margin: '8px 0 0 0'
    }
};

export default AddCAUser;
